import { useState, useEffect } from 'react';
import {
  SignInPageProps,
  useApi,
  useAnalytics,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { keycloakAuthApiRef } from '../../apis';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useSearchParams } from 'react-router-dom';
import { DotBackground } from './DotBackground';
import './SignInPage.css';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#0a0a0a',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    width: '100%',
    maxWidth: '400px',
    padding: '2rem',
  },
  containerHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    width: '100%',
    maxWidth: '800px',
    padding: '2rem',
  },
  title: {
    color: '#ffffff',
    fontSize: '3rem',
    fontWeight: 700,
    marginBottom: '0.5rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
  },
  titleLine: {
    display: 'block',
  },
  titleHighlight: {
    display: 'block',
    background: 'linear-gradient(90deg, #ffffff 0%, #4a9eff 50%, #ffffff 100%)',
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: '$highlightAnimation 3s ease-in-out infinite',
  },
  '@keyframes highlightAnimation': {
    '0%, 100%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
  },
  subtitle: {
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 400,
    marginBottom: '2.5rem',
    textAlign: 'center',
    opacity: 0.9,
  },
  signInButton: {
    width: '100%',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    borderRadius: '8px',
    padding: '14px 20px',
    fontSize: '1rem',
    fontWeight: 500,
    textTransform: 'none',
    border: '1px solid #2a2a2a',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#252525',
      borderColor: '#3a3a3a',
      transform: 'translateY(-1px)',
    },
  },
  buttonIcon: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  errorText: {
    color: '#ff4444',
    fontSize: '0.875rem',
    marginTop: '1rem',
    textAlign: 'center',
  },
  footer: {
    marginTop: '3rem',
    textAlign: 'center',
    color: '#888888',
    fontSize: '0.75rem',
    lineHeight: '1.5',
  },
  footerLink: {
    color: '#aaaaaa',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const CustomSignInPage = (props: SignInPageProps) => {
  const classes = useStyles();
  const authApi = useApi(keycloakAuthApiRef);
  const analytics = useAnalytics();
  const [error, setError] = useState<Error | undefined>();
  const [isChecking, setIsChecking] = useState(true);
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  // Check for existing session silently on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        if (errorParam) {
          setError(new Error(errorParam));
          setIsChecking(false);
          return;
        }

        // Silently check for existing identity without showing popup
        const identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });

        if (identityResponse) {
          // User is already signed in, proceed with success
          const profile = await authApi.getProfile();
          props.onSignInSuccess(
            UserIdentity.create({
              identity: identityResponse.identity,
              authApi,
              profile,
            }),
          );
          analytics.captureEvent('signIn', 'success');
          return;
        }

        // No existing session, show login page
        setIsChecking(false);
      } catch (err) {
        setError(err as Error);
        setIsChecking(false);
      }
    };

    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = async () => {
    try {
      setError(undefined);
      // Show popup only when button is clicked
      const identityResponse = await authApi.getBackstageIdentity({
        instantPopup: true,
      });

      if (!identityResponse) {
        throw new Error(
          'The Keycloak provider is not configured to support sign-in',
        );
      }

      const profile = await authApi.getProfile();
      props.onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
      analytics.captureEvent('signIn', 'success');
    } catch (err) {
      // Don't show error for popup rejection
      if ((err as Error).name !== 'PopupRejectedError') {
        setError(err as Error);
      }
    }
  };

  // Show loading state while checking for existing session
  if (isChecking) {
    return null;
  }

  return (
    <div className={classes.root}>
      <DotBackground />
      <div className={classes.containerHeader}>
        <Typography className={classes.title}>
          <span className={classes.titleLine}>Welcome to</span>
          <span className={classes.titleHighlight}>Eigen Developer Portal</span>
        </Typography>
      </div>
      <div className={classes.container}>
        <Button
          className={classes.signInButton}
          onClick={handleSignIn}
          variant="contained"
        >
          Sign in with Keycloak
        </Button>

        {error && error.name !== 'PopupRejectedError' && (
          <Typography className={classes.errorText}>{error.message}</Typography>
        )}

        <Typography className={classes.footer}>
          By signing up, you agree to the{' '}
          <a href="/terms/product" className={classes.footerLink}>
            Product Terms
          </a>
          ,{' '}
          <a href="/terms/policies" className={classes.footerLink}>
            Policies
          </a>
          ,{' '}
          <a href="/terms/privacy" className={classes.footerLink}>
            Privacy Notice
          </a>
          , and{' '}
          <a href="/terms/cookies" className={classes.footerLink}>
            Cookie Notice
          </a>
          .
        </Typography>
      </div>
    </div>
  );
};
